import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useConditionalDebounce } from '../../../../_helpers/hooks/useDebounce';
import { Autocomplete, SearchBarProps } from './SearchBarProps';
import SearchResults from './search-results/SearchResults';
import { getSubmitDataLinkEvent } from './_helpers/search-bar-data-link-events';
import { getAutocompleteUrl } from './_helpers/search-bar-urls';
import { useComponentContext } from '../_helpers/componentContext.tsx';

import './SearchBar.scss';

//Debounce delay for search autocomplete
const AUTOCOMPLETE_DEBOUNCE_DELAY = 500;

const SearchBar: React.FC<SearchBarProps> = ({ opened, dcr, i18n, config }) => {
	const componentName = useComponentContext().componentName;
	//Reference of the search HTMLInputElement
	const searchInputElement = useRef<HTMLInputElement>(null);
	//Reference of the search result component HTMLDivElement
	const searchResultContainerElement = useRef<HTMLDivElement>(null);

	//loading state for the search autocomplete http requests
	const [loading, setLoading] = useState<boolean>(false);
	//search input field value
	const [searchInput, setSearchInput] = useState<string>(dcr.searchInput || '');
	//search input field debounced value, if new input value is an empty string, debounce isn't applied
	const debouncedSearchInput = useConditionalDebounce<string>(
		searchInput,
		searchInput !== '',
		AUTOCOMPLETE_DEBOUNCE_DELAY
	);
	//search results state
	const [searchResults, setSearchResults] = useState<Autocomplete[]>([]);
	//index of the selected search results item for a11y reasons. Its value is -1 if no item is selected
	const [selectedItemIndex, setSelectedItemIndex] = useState<number>(-1);

	const [displaySearchResults, setDisplaySearchResults] =
		useState<boolean>(true);

	/**
	 * Handling opening/closing of search tray
	 */
	//Handles opened prop changing.
	//on Open: Focus on the input field
	//on Close: Removes search input and search results.
	useEffect(() => {
		if (opened) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			searchInputElement.current.focus();
		} else {
			setSearchInput('');
		}
	}, [opened]);

	//If the focus is removed from the input field or from the search results it hides the search results
	const onBlurOnInputOrResults = (
		event: React.FocusEvent<HTMLDivElement | HTMLInputElement, Element>
	) => {
		if (
			!event.relatedTarget ||
			(!searchResultContainerElement.current?.contains(
				event.relatedTarget as Element
			) &&
				searchInputElement.current != event.relatedTarget)
		) {
			setDisplaySearchResults(false);
		}
	};

	//If the input field receives the focus it shows the search results
	const onFocusInOnInput = () => {
		setDisplaySearchResults(true);
	};

	/**
	 * Handling search
	 */
	//Handles debounced search input value changes and sends autocomplete request (with abort signal)
	useEffect(() => {
		//if search input is an empty string doesn't send request
		if (!searchInput) {
			setSearchResults([]);
			return;
		}

		const abortController = new AbortController();

		const doAutocompleteSearch = async () => {
			setLoading(true);
			const response = await fetch(
				getAutocompleteUrl(config.autocompleteUrl, searchInput),
				{ signal: abortController?.signal }
			);
			if (!response.ok) {
				throw new Error(`Error! status: ${response.status}`);
			}
			const categories = Object.keys(config.categories);
			setSearchResults(orderByCategory(categories, await response.json()));
		};

		doAutocompleteSearch()
			.catch((err) => {
				if (!abortController?.signal?.aborted) {
					console.log(err);
				}
			})
			.finally(() => setLoading(false));

		return () => {
			abortController?.abort();
		};
	}, [debouncedSearchInput]);

	//if search results changes it resets selected search results item index
	useEffect(() => {
		setSelectedItemIndex(-1);
	}, [searchResults]);

	//on Input (on input field) sets the search input state
	const onInput = (event: React.FormEvent<HTMLInputElement>) => {
		setSearchInput((event.target as HTMLInputElement).value);
	};

	//on Submit redirect to the selected search results page or to the general search page
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const onSubmitForm = (event) => {
		event.preventDefault();
		const searchUrl: string =
			searchResults?.[selectedItemIndex]?.id ||
			`${dcr.resultPage}?searchText=${encodeURIComponent(
				searchInput
			)}&doTickerSearch=true`;
		window.location.assign(searchUrl);
	};

	//on click on Reset Button resets the input value and focuses on the input field
	const onClickOnReset = () => {
		//we force the search result to be empty so it doesn't wait for the next render round
		setSearchResults([]);
		setSearchInput('');
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		setTimeout(() => searchInputElement.current.focus(), 0);
	};

	/**
	 * Handling "select" search results
	 */
	//handles the up and down arrow keys on the input field
	const onKeyUpOnInput = (event: KeyboardEvent) => {
		if (event.key == 'ArrowDown' && searchResults.length) {
			event.preventDefault();
			setSelectedItemIndex((prev) =>
				prev === searchResults.length - 1 ? -1 : prev + 1
			);
		} else if (event.key == 'ArrowUp' && searchResults.length) {
			event.preventDefault();
			setSelectedItemIndex((prev) =>
				prev === -1 ? searchResults.length - 1 : prev - 1
			);
		}
	};

	return (
		<div
			id="main-navigation-search-tray"
			className="blk-ac-search-bar blk-ac-bg-white"
		>
			<div className="search-tray">
				<div className="search-tray-content" id="searchWrapper">
					<div className="search-form">
						<form id="searchForm" onSubmit={onSubmitForm} role="search">
							<fieldset>
								<div className="search-inline-container">
									<label htmlFor="searchText">
										<i className="fa fa-search"></i>
									</label>
									<input
										tabIndex={opened ? 0 : -1}
										type="text"
										id="searchText"
										name="searchText"
										className="search-input input-text"
										placeholder={i18n.searchInputPlaceholder}
										aria-label={i18n.searchInputPlaceholder}
										value={
											searchResults?.[selectedItemIndex]?.label || searchInput
										}
										autoComplete="off"
										ref={searchInputElement}
										onInput={onInput}
										onKeyUp={onKeyUpOnInput}
										onBlur={onBlurOnInputOrResults}
										onFocus={onFocusInOnInput}
									/>
									<label htmlFor="mainNavResetTxt">
										<span className="ui-helper-hidden-accessible">
											{i18n.searchFormResetLabel}
										</span>
									</label>
									{!!searchInput && (
										<button
											id="mainNavResetTxt"
											className="reset-button"
											type="reset"
											aria-label={i18n.searchAutocompleteResetLabel}
											onClick={onClickOnReset}
										>
											<span className="fa fa-close"></span>
										</button>
									)}
									<input
										className="search-button blk-ac-text-white blk-ac-body-m-bold"
										id="submit"
										type="submit"
										value={dcr.searchButtonLabel}
										disabled={!searchInput.length}
										aria-label={i18n.tickerSearchAriaLabel}
										data-link-event={getSubmitDataLinkEvent(
											componentName,
											searchInput
										)}
									/>
								</div>
							</fieldset>
						</form>
					</div>
				</div>

				<div
					className={`search-tray-loader-wrapper ${loading ? 'loading' : ''}`}
				>
					<div className="search-tray-loader"></div>
				</div>

				<SearchResults
					ref={searchResultContainerElement}
					searchInput={searchInput}
					searchResults={searchResults}
					searchConfig={config}
					selectedId={searchResults?.[selectedItemIndex]?.id}
					onBlur={onBlurOnInputOrResults}
					display={displaySearchResults}
				/>
			</div>
		</div>
	);
};
export default SearchBar;

//order the search results by category list order
const orderByCategory = (
	categoryList: string[],
	searchResults: Autocomplete[]
): Autocomplete[] => {
	let orderedResultList: Autocomplete[] = [];

	categoryList.map((category) => {
		const resultsByCategory = searchResults.filter(
			(result) => result.category === category
		);
		orderedResultList = [...orderedResultList, ...resultsByCategory];
	});
	return orderedResultList;
};
